import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ArrowLeft, Terminal, Bluetooth, Search, Loader2, Usb, Gamepad2, Keyboard, Headphones, Music, Play, Pause, Download, RefreshCw, Mic, MicOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mediaButtonService } from '../services/mediaButtonService';
import { init as initJabraSDK, webHidPairing, CallControlFactory, RequestedBrowserTransport } from '@gnaudio/jabra-js';
import { fetchAuthSession } from 'aws-amplify/auth';
import AppBarMenu from './AppBarMenu';
import { settings } from '../services/settingsService';
import { useVoskVoiceTrigger, shouldUseVosk } from '../hooks/useVoskVoiceTrigger';
import { getMicrophoneStream } from '../services/microphoneService';

const DebugConsole = ({ onLogout, isAdmin = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [eventLog, setEventLog] = useState([]);
  const logIdRef = useRef(0);

  // Web Bluetooth state
  const [bluetoothSupported, setBluetoothSupported] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [bluetoothDevice, setBluetoothDevice] = useState(null);
  const [bluetoothServices, setBluetoothServices] = useState([]);

  // Web HID state
  const [hidSupported, setHidSupported] = useState(false);
  const [hidScanning, setHidScanning] = useState(false);
  const [hidDevices, setHidDevices] = useState([]);

  // Gamepad API state
  const [gamepadSupported, setGamepadSupported] = useState(false);
  const [gamepadListening, setGamepadListening] = useState(false);
  const [connectedGamepads, setConnectedGamepads] = useState([]);
  const gamepadPollRef = useRef(null);
  const prevButtonStates = useRef({});

  // Media Keys state
  const [mediaKeysListening, setMediaKeysListening] = useState(false);

  // Jabra SDK state
  const [jabraInitializing, setJabraInitializing] = useState(false);
  const [jabraApi, setJabraApi] = useState(null);
  const [jabraDevices, setJabraDevices] = useState([]);
  const [jabraListening, setJabraListening] = useState(false);
  const jabraSubscriptionsRef = useRef([]);

  // Audio samples state
  const [audioSamples, setAudioSamples] = useState([]);
  const [audioLoading, setAudioLoading] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const audioRef = useRef(null);

  // Voice Trigger Test state
  const [voiceTestSupported, setVoiceTestSupported] = useState(false);
  const [voiceTestListening, setVoiceTestListening] = useState(false);
  const [voiceTestLanguage, setVoiceTestLanguage] = useState('de-DE');
  const voiceRecognitionRef = useRef(null);

  // Vosk Voice Test state (Android)
  const useVosk = shouldUseVosk();
  const [voskAudioContext, setVoskAudioContext] = useState(null);
  const [voskSourceNode, setVoskSourceNode] = useState(null);
  const [voskModelProgress, setVoskModelProgress] = useState(null);
  const [voskAudioMsgCount, setVoskAudioMsgCount] = useState(0);
  const [voskTranscriptCount, setVoskTranscriptCount] = useState(0);
  const voskStreamRef = useRef(null);

  // Check API support
  useEffect(() => {
    setBluetoothSupported('bluetooth' in navigator);
    setHidSupported('hid' in navigator);
    setGamepadSupported('getGamepads' in navigator);
    // Voice test is supported if Web Speech API is available OR we're on Android (Vosk)
    setVoiceTestSupported(!!(window.SpeechRecognition || window.webkitSpeechRecognition) || shouldUseVosk());
  }, []);

  const addLogEntry = (message) => {
    const timestamp = new Date().toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
    logIdRef.current += 1;
    const uniqueId = `${Date.now()}-${logIdRef.current}-${Math.random().toString(36).slice(2, 7)}`;
    setEventLog(prev => [{
      id: uniqueId,
      timestamp,
      message
    }, ...prev].slice(0, 50)); // Keep last 50 entries
  };

  const handleClearLog = () => {
    console.log('Clearing event log');
    setEventLog([]);
  };

  // Web Bluetooth: Scan for devices
  const scanBluetoothDevices = async () => {
    if (!bluetoothSupported) {
      addLogEntry('Web Bluetooth not supported in this browser');
      return;
    }

    setScanning(true);
    addLogEntry('Starting Bluetooth scan...');

    try {
      // Note: The browser picker UI cannot be filtered from JavaScript.
      // "Unknown or Unsupported Device" entries are a browser limitation.
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          'battery_service',
          'device_information',
          'generic_access',
          'generic_attribute',
          '00001812-0000-1000-8000-00805f9b34fb', // HID Service
          '00001801-0000-1000-8000-00805f9b34fb', // Generic Attribute
          '00001800-0000-1000-8000-00805f9b34fb', // Generic Access
        ]
      });

      addLogEntry(`Device selected: ${device.name || 'Unknown'} (ID: ${device.id})`);
      setBluetoothDevice(device);

      // Connect to GATT server
      addLogEntry('Connecting to GATT server...');
      const server = await device.gatt.connect();
      addLogEntry('Connected to GATT server');

      // Discover all services
      addLogEntry('Discovering services...');
      const services = await server.getPrimaryServices();
      addLogEntry(`Found ${services.length} service(s)`);

      const serviceDetails = [];

      for (const service of services) {
        addLogEntry(`Service: ${service.uuid}`);

        try {
          const characteristics = await service.getCharacteristics();
          const charDetails = [];

          for (const char of characteristics) {
            const props = [];
            if (char.properties.read) props.push('read');
            if (char.properties.write) props.push('write');
            if (char.properties.notify) props.push('notify');
            if (char.properties.indicate) props.push('indicate');

            addLogEntry(`  Characteristic: ${char.uuid} [${props.join(', ')}]`);

            charDetails.push({
              uuid: char.uuid,
              properties: props
            });

            // Try to subscribe to notifications for button events
            if (char.properties.notify) {
              try {
                await char.startNotifications();
                char.addEventListener('characteristicvaluechanged', (event) => {
                  const value = event.target.value;
                  const bytes = [];
                  for (let i = 0; i < value.byteLength; i++) {
                    bytes.push(value.getUint8(i).toString(16).padStart(2, '0'));
                  }
                  addLogEntry(`NOTIFY ${char.uuid}: [${bytes.join(' ')}]`);
                });
                addLogEntry(`  Subscribed to notifications on ${char.uuid}`);
              } catch (notifyErr) {
                addLogEntry(`  Could not subscribe: ${notifyErr.message}`);
              }
            }

            // Try to read value
            if (char.properties.read) {
              try {
                const value = await char.readValue();
                const bytes = [];
                for (let i = 0; i < value.byteLength; i++) {
                  bytes.push(value.getUint8(i).toString(16).padStart(2, '0'));
                }
                addLogEntry(`  Value: [${bytes.join(' ')}]`);
              } catch (readErr) {
                addLogEntry(`  Could not read: ${readErr.message}`);
              }
            }
          }

          serviceDetails.push({
            uuid: service.uuid,
            characteristics: charDetails
          });
        } catch (charErr) {
          addLogEntry(`  Error getting characteristics: ${charErr.message}`);
        }
      }

      setBluetoothServices(serviceDetails);

      // Listen for disconnection
      device.addEventListener('gattserverdisconnected', () => {
        addLogEntry('Device disconnected');
        setBluetoothDevice(null);
        setBluetoothServices([]);
      });

    } catch (err) {
      if (err.name === 'NotFoundError') {
        addLogEntry('No device selected');
      } else {
        addLogEntry(`Bluetooth error: ${err.message}`);
      }
    } finally {
      setScanning(false);
    }
  };

  // Disconnect Bluetooth device
  const disconnectBluetooth = () => {
    if (bluetoothDevice && bluetoothDevice.gatt.connected) {
      bluetoothDevice.gatt.disconnect();
      addLogEntry('Disconnected from device');
    }
    setBluetoothDevice(null);
    setBluetoothServices([]);
  };

  // Web HID: Scan for HID devices
  const scanHidDevices = async () => {
    if (!hidSupported) {
      addLogEntry('Web HID not supported in this browser');
      return;
    }

    setHidScanning(true);
    addLogEntry('Requesting HID device access...');

    try {
      // Request any HID device
      const devices = await navigator.hid.requestDevice({
        filters: [] // Empty filters = show all HID devices
      });

      if (devices.length === 0) {
        addLogEntry('No HID device selected');
        setHidScanning(false);
        return;
      }

      for (const device of devices) {
        addLogEntry(`HID Device: ${device.productName} (VID: 0x${device.vendorId.toString(16)}, PID: 0x${device.productId.toString(16)})`);

        // Try to open the device
        if (!device.opened) {
          try {
            await device.open();
            addLogEntry(`Opened: ${device.productName}`);
          } catch (openErr) {
            addLogEntry(`Could not open device: ${openErr.message}`);
            continue;
          }
        }

        // Log collections (input/output/feature reports)
        addLogEntry(`  Collections: ${device.collections.length}`);
        device.collections.forEach((col, i) => {
          addLogEntry(`    [${i}] Usage: 0x${col.usage.toString(16)}, UsagePage: 0x${col.usagePage.toString(16)}`);
          if (col.inputReports) {
            col.inputReports.forEach(report => {
              addLogEntry(`      Input Report ID: ${report.reportId}, Items: ${report.items?.length || 0}`);
            });
          }
        });

        // Listen for input reports (button presses, etc.)
        device.addEventListener('inputreport', (event) => {
          const { data, reportId } = event;
          const bytes = [];
          for (let i = 0; i < data.byteLength; i++) {
            bytes.push(data.getUint8(i).toString(16).padStart(2, '0'));
          }
          addLogEntry(`HID INPUT [${device.productName}] Report ${reportId}: [${bytes.join(' ')}]`);
        });

        addLogEntry(`Listening for input from: ${device.productName}`);
      }

      setHidDevices(devices);

    } catch (err) {
      if (err.name === 'NotAllowedError') {
        addLogEntry('HID access denied by user');
      } else {
        addLogEntry(`HID error: ${err.message}`);
      }
    } finally {
      setHidScanning(false);
    }
  };

  // Close HID devices
  const closeHidDevices = async () => {
    for (const device of hidDevices) {
      if (device.opened) {
        await device.close();
        addLogEntry(`Closed: ${device.productName}`);
      }
    }
    setHidDevices([]);
  };

  // Gamepad API: Poll for connected gamepads and button presses
  const pollGamepads = useCallback(() => {
    const gamepads = navigator.getGamepads();
    const connected = [];

    for (let i = 0; i < gamepads.length; i++) {
      const gp = gamepads[i];
      if (!gp) continue;

      connected.push({
        index: gp.index,
        id: gp.id,
        buttons: gp.buttons.length,
        axes: gp.axes.length,
        mapping: gp.mapping
      });

      // Check for button state changes
      const gpKey = `gp${gp.index}`;
      if (!prevButtonStates.current[gpKey]) {
        prevButtonStates.current[gpKey] = new Array(gp.buttons.length).fill(false);
      }

      for (let b = 0; b < gp.buttons.length; b++) {
        const isPressed = gp.buttons[b].pressed;
        const wasPressed = prevButtonStates.current[gpKey][b];

        if (isPressed && !wasPressed) {
          addLogEntry(`GAMEPAD [${gp.id}] Button ${b} PRESSED (value: ${gp.buttons[b].value.toFixed(2)})`);
        } else if (!isPressed && wasPressed) {
          addLogEntry(`GAMEPAD [${gp.id}] Button ${b} RELEASED`);
        }

        prevButtonStates.current[gpKey][b] = isPressed;
      }
    }

    setConnectedGamepads(connected);
  }, []);

  const startGamepadListening = () => {
    if (!gamepadSupported) {
      addLogEntry('Gamepad API not supported in this browser');
      return;
    }

    addLogEntry('Starting Gamepad listener...');
    setGamepadListening(true);

    // Initial poll
    pollGamepads();

    // Poll at 60fps
    gamepadPollRef.current = setInterval(pollGamepads, 16);

    // Listen for gamepad connect/disconnect
    window.addEventListener('gamepadconnected', handleGamepadConnected);
    window.addEventListener('gamepaddisconnected', handleGamepadDisconnected);
  };

  const stopGamepadListening = () => {
    if (gamepadPollRef.current) {
      clearInterval(gamepadPollRef.current);
      gamepadPollRef.current = null;
    }
    window.removeEventListener('gamepadconnected', handleGamepadConnected);
    window.removeEventListener('gamepaddisconnected', handleGamepadDisconnected);
    setGamepadListening(false);
    setConnectedGamepads([]);
    prevButtonStates.current = {};
    addLogEntry('Gamepad listener stopped');
  };

  const handleGamepadConnected = (e) => {
    addLogEntry(`GAMEPAD CONNECTED: ${e.gamepad.id} (index: ${e.gamepad.index}, buttons: ${e.gamepad.buttons.length}, axes: ${e.gamepad.axes.length})`);
  };

  const handleGamepadDisconnected = (e) => {
    addLogEntry(`GAMEPAD DISCONNECTED: ${e.gamepad.id}`);
  };

  // Media Keys: Listen for keyboard media key events
  const handleMediaKey = useCallback((e) => {
    const mediaKeys = ['MediaPlayPause', 'MediaPlay', 'MediaPause', 'MediaStop', 'MediaTrackNext', 'MediaTrackPrevious', 'MediaRewind', 'MediaFastForward'];

    if (mediaKeys.includes(e.key) || mediaKeys.includes(e.code)) {
      addLogEntry(`MEDIA KEY: ${e.key || e.code} (keyCode: ${e.keyCode})`);
      // Don't prevent default - let the system handle it
    }

    // Also log any key press for debugging
    if (e.key.startsWith('Media') || e.code.startsWith('Media') || (e.keyCode >= 166 && e.keyCode <= 179)) {
      addLogEntry(`KEY EVENT: key="${e.key}" code="${e.code}" keyCode=${e.keyCode}`);
    }
  }, []);

  const startMediaKeysListening = () => {
    addLogEntry('Starting Media Keys listener...');
    addLogEntry('Playing silent audio to claim media focus...');
    setMediaKeysListening(true);

    document.addEventListener('keydown', handleMediaKey);
    document.addEventListener('keyup', (e) => {
      if (e.key.startsWith('Media')) {
        addLogEntry(`MEDIA KEY UP: ${e.key}`);
      }
    });

    // Use mediaButtonService to play silent audio and register handlers
    if (mediaButtonService.isSupported()) {
      mediaButtonService.startListening((action) => {
        addLogEntry(`MediaSession: ${action.toUpperCase()} action triggered`);
      });
      addLogEntry('MediaSession handlers registered with silent audio');
    } else {
      addLogEntry('MediaSession API not available');
    }
  };

  const stopMediaKeysListening = () => {
    document.removeEventListener('keydown', handleMediaKey);
    mediaButtonService.stopListening();
    setMediaKeysListening(false);
    addLogEntry('Media Keys listener stopped');
  };

  // Jabra SDK: Initialize and scan for devices
  const initializeJabra = async () => {
    setJabraInitializing(true);
    addLogEntry('Initializing Jabra SDK...');

    try {
      // First, trigger WebHID pairing dialog
      addLogEntry('Opening WebHID pairing dialog...');
      await webHidPairing();
      addLogEntry('WebHID pairing completed');

      // Initialize the SDK with WebHID transport
      const api = await initJabraSDK({
        appId: 'holocron-pwa',
        appName: 'Holocron Debug',
        transport: RequestedBrowserTransport.WEB_HID
      });

      setJabraApi(api);
      addLogEntry('Jabra SDK initialized successfully');

      // Subscribe to device list changes
      const deviceSub = api.deviceList.subscribe({
        next: (devices) => {
          addLogEntry(`Jabra devices found: ${devices.length}`);
          setJabraDevices(devices);

          devices.forEach(device => {
            addLogEntry(`  - ${device.name} (${device.productId})`);
          });
        },
        error: (err) => {
          addLogEntry(`Jabra device list error: ${err.message}`);
        }
      });

      jabraSubscriptionsRef.current.push(deviceSub);
      setJabraListening(true);

    } catch (err) {
      addLogEntry(`Jabra SDK error: ${err.message}`);
      console.error('Jabra SDK error:', err);
    } finally {
      setJabraInitializing(false);
    }
  };

  const subscribeToJabraButtons = async (device) => {
    addLogEntry(`Subscribing to events for: ${device.name}`);
    addLogEntry(`Device info: id=${device.id}, vendorId=${device.vendorId}, productId=${device.productId}, type=${device.type}`);

    try {
      if (!jabraApi) {
        addLogEntry('Jabra API not initialized');
        return;
      }

      // Log available device properties
      addLogEntry(`Device properties: ${Object.keys(device).join(', ')}`);

      // Subscribe to new connections
      if (device.connectionAdded) {
        addLogEntry('Subscribing to device.connectionAdded...');
        const connSub = device.connectionAdded.subscribe({
          next: (connection) => {
            addLogEntry(`CONNECTION ADDED: ${JSON.stringify(Object.keys(connection))}`);

            // Try to get HID channel from connection
            if (connection.hasChannel && connection.hasChannel('hid')) {
              addLogEntry('Connection has HID channel, subscribing to input reports...');
              const hidChannel = connection.getChannel('hid');

              if (hidChannel && hidChannel.inputReports) {
                const hidSub = hidChannel.inputReports.subscribe({
                  next: (report) => {
                    const data = Array.from(new Uint8Array(report.data || [])).map(b => b.toString(16).padStart(2, '0')).join(' ');
                    addLogEntry(`HID INPUT [${device.name}]: reportId=${report.reportId} data=[${data}]`);
                  },
                  error: (err) => addLogEntry(`HID error: ${err.message}`)
                });
                jabraSubscriptionsRef.current.push(hidSub);
                addLogEntry('Subscribed to HID input reports');
              }
            }
          }
        });
        jabraSubscriptionsRef.current.push(connSub);
      }

      // Get existing connections
      const connections = device.connections || [];
      addLogEntry(`Existing connections: ${connections.length}`);

      // Try to access HID channel directly if device has getChannel
      if (device.hasChannel && device.hasChannel('hid')) {
        addLogEntry('Device has HID channel directly');
        try {
          const hidChannel = device.getChannel('hid');
          addLogEntry(`HID channel properties: ${Object.keys(hidChannel).join(', ')}`);

          if (hidChannel.inputReports) {
            addLogEntry('Subscribing to HID inputReports...');
            const hidSub = hidChannel.inputReports.subscribe({
              next: (report) => {
                const data = Array.from(new Uint8Array(report.data || [])).map(b => b.toString(16).padStart(2, '0')).join(' ');
                addLogEntry(`HID INPUT [${device.name}]: reportId=${report.reportId} data=[${data}]`);
              },
              error: (err) => addLogEntry(`HID inputReports error: ${err.message}`)
            });
            jabraSubscriptionsRef.current.push(hidSub);
            addLogEntry('Subscribed to HID input reports');
          }
        } catch (hidErr) {
          addLogEntry(`HID channel error: ${hidErr.message}`);
        }
      } else {
        addLogEntry('Device does not have HID channel directly');
      }

      // Try EasyCallControlFactory as fallback
      try {
        addLogEntry('Trying EasyCallControlFactory...');
        const { EasyCallControlFactory } = await import('@gnaudio/jabra-js');
        const eccFactory = new EasyCallControlFactory(jabraApi);
        const ecc = await eccFactory.createSingleCallControl(device);
        addLogEntry('EasyCallControl created');
        addLogEntry(`EasyCallControl properties: ${Object.keys(ecc).join(', ')}`);

        // Subscribe to mute state changes
        if (ecc.muteState) {
          const muteSub = ecc.muteState.subscribe({
            next: (state) => addLogEntry(`MUTE STATE: ${state}`)
          });
          jabraSubscriptionsRef.current.push(muteSub);
        }
      } catch (eccErr) {
        addLogEntry(`EasyCallControl not supported: ${eccErr.message}`);
      }

    } catch (err) {
      addLogEntry(`Error subscribing to ${device.name}: ${err.message}`);
      console.error('Jabra subscribe error:', err);
    }
  };

  const stopJabraListening = () => {
    addLogEntry('Stopping Jabra SDK...');

    // Unsubscribe from all subscriptions
    jabraSubscriptionsRef.current.forEach(sub => {
      if (sub && typeof sub.unsubscribe === 'function') {
        sub.unsubscribe();
      }
    });
    jabraSubscriptionsRef.current = [];

    setJabraApi(null);
    setJabraDevices([]);
    setJabraListening(false);
    addLogEntry('Jabra SDK stopped');
  };

  // Voice Trigger Test: Start speech recognition
  const startVoiceTest = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      addLogEntry('Speech Recognition not supported');
      return;
    }

    // Stop any existing recognition
    if (voiceRecognitionRef.current) {
      try {
        voiceRecognitionRef.current.stop();
      } catch (e) { /* ignore */ }
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = voiceTestLanguage;
    recognition.maxAlternatives = 3;

    const startKeywords = settings.voiceTrigger.getKeywords();
    const stopKeywords = settings.stopTrigger.getKeywords();

    addLogEntry(`=== Voice Test Started ===`);
    addLogEntry(`Language: ${voiceTestLanguage}`);
    addLogEntry(`START keywords: ${startKeywords.join(', ')}`);
    addLogEntry(`STOP keywords: ${stopKeywords.join(', ')}`);

    recognition.onstart = () => {
      addLogEntry('[EVENT] onstart - Recognition started');
      setVoiceTestListening(true);
    };

    recognition.onaudiostart = () => {
      addLogEntry('[EVENT] onaudiostart - Audio capture started');
    };

    recognition.onaudioend = () => {
      addLogEntry('[EVENT] onaudioend - Audio capture ended');
    };

    recognition.onsoundstart = () => {
      addLogEntry('[EVENT] onsoundstart - Sound detected');
    };

    recognition.onsoundend = () => {
      addLogEntry('[EVENT] onsoundend - Sound ended');
    };

    recognition.onspeechstart = () => {
      addLogEntry('[EVENT] onspeechstart - Speech detected');
    };

    recognition.onspeechend = () => {
      addLogEntry('[EVENT] onspeechend - Speech ended');
    };

    recognition.onnomatch = () => {
      addLogEntry('[EVENT] onnomatch - No match');
    };

    recognition.onresult = (event) => {
      const lastResult = event.results[event.results.length - 1];
      const transcript = lastResult[0].transcript.toLowerCase().trim();
      const confidence = (lastResult[0].confidence * 100).toFixed(1);
      const isFinal = lastResult.isFinal;

      // Check for keyword matches
      const matchedStart = startKeywords.find(kw =>
        transcript.includes(kw) || kw.split(/\s+/).every(w => transcript.includes(w))
      );
      const matchedStop = stopKeywords.find(kw =>
        transcript.includes(kw) || kw.split(/\s+/).every(w => transcript.includes(w))
      );

      let logMsg = `[TRANSCRIPT${isFinal ? ' FINAL' : ''}] "${transcript}" (${confidence}%)`;
      if (matchedStart) {
        logMsg += ` >>> MATCHED START: "${matchedStart}"`;
      }
      if (matchedStop) {
        logMsg += ` >>> MATCHED STOP: "${matchedStop}"`;
      }
      addLogEntry(logMsg);

      // Show alternatives if available
      if (lastResult.length > 1 && isFinal) {
        for (let i = 1; i < lastResult.length; i++) {
          const alt = lastResult[i];
          addLogEntry(`  [ALT ${i}] "${alt.transcript}" (${(alt.confidence * 100).toFixed(1)}%)`);
        }
      }
    };

    recognition.onerror = (event) => {
      addLogEntry(`[ERROR] ${event.error}`);
    };

    recognition.onend = () => {
      addLogEntry('[EVENT] onend - Recognition ended');
      // Auto-restart if still listening
      if (voiceRecognitionRef.current) {
        addLogEntry('[AUTO] Restarting recognition...');
        setTimeout(() => {
          if (voiceRecognitionRef.current) {
            try {
              recognition.start();
            } catch (e) {
              addLogEntry(`[ERROR] Restart failed: ${e.message}`);
              setVoiceTestListening(false);
              voiceRecognitionRef.current = null;
            }
          }
        }, 100);
      } else {
        setVoiceTestListening(false);
      }
    };

    voiceRecognitionRef.current = recognition;

    try {
      recognition.start();
    } catch (e) {
      addLogEntry(`[ERROR] Start failed: ${e.message}`);
      voiceRecognitionRef.current = null;
    }
  };

  const stopVoiceTest = () => {
    addLogEntry('=== Voice Test Stopped ===');
    if (voiceRecognitionRef.current) {
      try {
        voiceRecognitionRef.current.stop();
      } catch (e) { /* ignore */ }
      voiceRecognitionRef.current = null;
    }
    setVoiceTestListening(false);
  };

  // Vosk Voice Test: Start (Android)
  const startVoskVoiceTest = async () => {
    addLogEntry('=== Vosk Voice Test Starting ===');
    addLogEntry('Platform: Android (using Vosk Wasm)');

    try {
      // Get mic stream
      const stream = await getMicrophoneStream();
      voskStreamRef.current = stream;

      // Create AudioContext
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      if (audioCtx.state === 'suspended') {
        await audioCtx.resume();
      }

      // Create source node from mic stream
      const source = audioCtx.createMediaStreamSource(stream);

      setVoskAudioContext(audioCtx);
      setVoskSourceNode(source);
      setVoiceTestListening(true);

      addLogEntry(`AudioContext ready (${audioCtx.sampleRate}Hz)`);
      addLogEntry('Vosk model will be loaded if not cached...');

    } catch (err) {
      addLogEntry(`[ERROR] Vosk setup failed: ${err.message}`);
    }
  };

  // Vosk Voice Test: Stop (Android)
  const stopVoskVoiceTest = () => {
    addLogEntry('=== Vosk Voice Test Stopped ===');

    if (voskStreamRef.current) {
      voskStreamRef.current.getTracks().forEach(t => t.stop());
      voskStreamRef.current = null;
    }

    if (voskAudioContext && voskAudioContext.state !== 'closed') {
      voskAudioContext.close().catch(() => {});
    }

    setVoskAudioContext(null);
    setVoskSourceNode(null);
    setVoiceTestListening(false);
    setVoskAudioMsgCount(0);
    setVoskTranscriptCount(0);
  };

  // Vosk keyword detection callback
  const handleVoskKeywordDetected = useCallback((keyword) => {
    addLogEntry(`>>> VOSK MATCHED: "${keyword}"`);
  }, []);

  // Memoize keywords to prevent infinite loop from array recreation
  const voskKeywords = useMemo(() => {
    return [...settings.voiceTrigger.getKeywords(), ...settings.stopTrigger.getKeywords()];
  }, []); // Keywords don't change during session

  // Debug: Log hook inputs (only once when listening starts)
  const prevVoiceTestListeningRef = useRef(false);
  useEffect(() => {
    // Only log once when voice test starts, not on every state change
    if (useVosk && voiceTestListening && !prevVoiceTestListeningRef.current) {
      addLogEntry(`[DEBUG] Hook inputs: enabled=${useVosk && voiceTestListening && voskAudioContext !== null}, hasContext=${!!voskAudioContext}, hasSource=${!!voskSourceNode}`);
    }
    prevVoiceTestListeningRef.current = voiceTestListening;
  }, [useVosk, voiceTestListening, voskAudioContext, voskSourceNode]);

  // Vosk hook for voice test (Android)
  const {
    isListening: isVoskListening,
    isModelReady: isVoskModelReady,
    isModelLoading: isVoskModelLoading,
    lastHeardText: voskLastHeardText,
    error: voskError,
    audioMessageCount: voskAudioCount,
    debugInfo: voskDebugInfo
  } = useVoskVoiceTrigger({
    enabled: useVosk && voiceTestListening && voskAudioContext !== null,
    audioContext: voskAudioContext,
    sourceNode: voskSourceNode,
    keywords: voskKeywords,
    onKeywordDetected: handleVoskKeywordDetected,
    onModelLoadProgress: (percent) => {
      setVoskModelProgress(percent);
      addLogEntry(`[VOSK] Model loading: ${percent}%`);
    },
    onTranscript: (text, isFinal) => {
      // Track transcript count for UI diagnostics
      setVoskTranscriptCount(prev => prev + 1);

      const startKeywords = settings.voiceTrigger.getKeywords();
      const stopKeywords = settings.stopTrigger.getKeywords();

      // Check for matches
      const matchedStart = startKeywords.find(kw =>
        text.toLowerCase().includes(kw) || kw.split(/\s+/).every(w => text.toLowerCase().includes(w))
      );
      const matchedStop = stopKeywords.find(kw =>
        text.toLowerCase().includes(kw) || kw.split(/\s+/).every(w => text.toLowerCase().includes(w))
      );

      let logMsg = `[VOSK${isFinal ? ' FINAL' : ''}] "${text}"`;
      if (matchedStart) logMsg += ` >>> MATCHED START: "${matchedStart}"`;
      if (matchedStop) logMsg += ` >>> MATCHED STOP: "${matchedStop}"`;
      addLogEntry(logMsg);
    },
    onError: (err) => {
      addLogEntry(`[VOSK ERROR] ${err.message}`);
    }
  });

  // Debug: Log hook state changes (only when state actually changes)
  const prevHookStateRef = useRef({ isVoskListening: false, isVoskModelReady: false, isVoskModelLoading: false });
  useEffect(() => {
    if (useVosk && voiceTestListening) {
      const prev = prevHookStateRef.current;
      // Only log if there's an actual change in hook state
      if (prev.isVoskListening !== isVoskListening ||
          prev.isVoskModelReady !== isVoskModelReady ||
          prev.isVoskModelLoading !== isVoskModelLoading) {
        addLogEntry(`[DEBUG] Hook state: isListening=${isVoskListening}, isModelReady=${isVoskModelReady}, isModelLoading=${isVoskModelLoading}, error=${voskError?.message || 'none'}`);
        prevHookStateRef.current = { isVoskListening, isVoskModelReady, isVoskModelLoading };
      }
    }
  }, [useVosk, voiceTestListening, isVoskListening, isVoskModelReady, isVoskModelLoading, voskError]);

  // Combined start/stop for voice test (auto-selects Vosk on Android)
  const handleStartVoiceTest = () => {
    if (useVosk) {
      startVoskVoiceTest();
    } else {
      startVoiceTest();
    }
  };

  const handleStopVoiceTest = () => {
    if (useVosk) {
      stopVoskVoiceTest();
    } else {
      stopVoiceTest();
    }
  };

  // Audio Samples: Load from API
  const loadAudioSamples = async () => {
    setAudioLoading(true);
    addLogEntry('Loading audio samples...');

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        addLogEntry('Not authenticated - cannot load audio samples');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/debug/audio-samples`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      addLogEntry(`Found ${data.samples.length} audio sample(s)`);
      setAudioSamples(data.samples);
    } catch (err) {
      addLogEntry(`Error loading audio samples: ${err.message}`);
      console.error('API error:', err);
    } finally {
      setAudioLoading(false);
    }
  };

  // Auto-load audio samples on mount
  useEffect(() => {
    // Small delay to ensure auth is ready
    const timer = setTimeout(() => {
      loadAudioSamples();
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const playAudioSample = (sample) => {
    if (currentlyPlaying === sample.key) {
      // Pause if already playing
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setCurrentlyPlaying(null);
      addLogEntry(`Paused: ${sample.name}`);
    } else {
      // Play new sample
      if (audioRef.current) {
        audioRef.current.pause();
      }
      audioRef.current = new Audio(sample.url);
      audioRef.current.play();
      audioRef.current.onended = () => setCurrentlyPlaying(null);
      setCurrentlyPlaying(sample.key);
      addLogEntry(`Playing: ${sample.name}`);
    }
  };

  const downloadAudioSample = async (sample) => {
    addLogEntry(`Downloading: ${sample.name}`);
    try {
      const response = await fetch(sample.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = sample.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      addLogEntry(`Download complete: ${sample.name}`);
    } catch (err) {
      addLogEntry(`Download failed: ${err.message}`);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (gamepadPollRef.current) {
        clearInterval(gamepadPollRef.current);
      }
      window.removeEventListener('gamepadconnected', handleGamepadConnected);
      window.removeEventListener('gamepaddisconnected', handleGamepadDisconnected);

      // Cleanup Jabra subscriptions
      jabraSubscriptionsRef.current.forEach(sub => {
        if (sub && typeof sub.unsubscribe === 'function') {
          sub.unsubscribe();
        }
      });

      // Cleanup audio player
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      // Cleanup voice recognition
      if (voiceRecognitionRef.current) {
        try {
          voiceRecognitionRef.current.stop();
        } catch (e) { /* ignore */ }
        voiceRecognitionRef.current = null;
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="app-bar">
        <button onClick={() => navigate(-1)} className="cursor-pointer hover:opacity-80 mr-2">
          <ArrowLeft size={24} className="text-white" />
        </button>
        <Terminal size={24} className="text-white" />
        <h1 className="flex-1 text-white font-normal text-xl ml-2">{t('debug.title')}</h1>
        {onLogout && <AppBarMenu onLogout={onLogout} isAdmin={isAdmin} />}
      </header>

      <main className="w-full px-4 lg:px-8 py-8 space-y-6">
        {/* Connection Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Web Bluetooth Card */}
        <div className="card-elevated p-4">
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            <Bluetooth size={20} />
            {t('debug.bluetooth_api')}
          </h2>

          {!bluetoothSupported ? (
            <p className="text-amber-600">
              {t('debug.bluetooth_not_supported')}
            </p>
          ) : bluetoothDevice ? (
            <div>
              <p className="text-green-600 font-medium mb-2">
                {t('debug.connected')}: {bluetoothDevice.name || t('debug.unknown_device')}
              </p>
              <button
                onClick={disconnectBluetooth}
                className="btn-secondary text-sm"
              >
                {t('debug.disconnect')}
              </button>

              {bluetoothServices.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-medium text-text-muted mb-2">
                    {t('debug.services')} ({bluetoothServices.length})
                  </h3>
                  {bluetoothServices.map((svc, svcIdx) => (
                    <div key={`${svc.uuid}-${svcIdx}`} className="mb-2 text-xs font-mono bg-gray-100 p-2 rounded">
                      <div className="font-semibold">{svc.uuid}</div>
                      {svc.characteristics.map((char, charIdx) => (
                        <div key={`${char.uuid}-${charIdx}`} className="ml-4 text-gray-600">
                          {char.uuid} [{char.properties.join(', ')}]
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <p className="text-text-muted mb-3">
                {t('debug.scan_ble_description')}
              </p>
              <button
                onClick={scanBluetoothDevices}
                disabled={scanning}
                className="btn-primary flex items-center gap-2"
              >
                {scanning ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {t('debug.scanning')}
                  </>
                ) : (
                  <>
                    <Search size={16} />
                    {t('debug.scan_bluetooth')}
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Web HID Card */}
        <div className="card-elevated p-4">
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            <Usb size={20} />
            {t('debug.hid_api')}
          </h2>

          {!hidSupported ? (
            <p className="text-amber-600">
              {t('debug.hid_not_supported')}
            </p>
          ) : hidDevices.length > 0 ? (
            <div>
              <p className="text-green-600 font-medium mb-2">
                {t('debug.hid_devices_connected', { count: hidDevices.length })}
              </p>
              <ul className="text-sm mb-3">
                {hidDevices.map((device, i) => (
                  <li key={i} className="text-text-muted">
                    {device.productName} (VID: 0x{device.vendorId.toString(16)}, PID: 0x{device.productId.toString(16)})
                  </li>
                ))}
              </ul>
              <button
                onClick={closeHidDevices}
                className="btn-secondary text-sm"
              >
                {t('debug.disconnect')}
              </button>
            </div>
          ) : (
            <div>
              <p className="text-text-muted mb-3">
                {t('debug.scan_hid_description')}
              </p>
              <button
                onClick={scanHidDevices}
                disabled={hidScanning}
                className="btn-primary flex items-center gap-2"
              >
                {hidScanning ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {t('debug.scanning')}
                  </>
                ) : (
                  <>
                    <Search size={16} />
                    {t('debug.scan_hid')}
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Gamepad API Card */}
        <div className="card-elevated p-4">
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            <Gamepad2 size={20} />
            {t('debug.gamepad_api')}
          </h2>

          {!gamepadSupported ? (
            <p className="text-amber-600">
              {t('debug.gamepad_not_supported')}
            </p>
          ) : gamepadListening ? (
            <div>
              <p className="text-green-600 font-medium mb-2">
                {t('debug.gamepad_listening')}
              </p>
              {connectedGamepads.length > 0 ? (
                <ul className="text-sm mb-3">
                  {connectedGamepads.map((gp, i) => (
                    <li key={i} className="text-text-muted">
                      {gp.id} ({gp.buttons} buttons, {gp.axes} axes)
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-text-muted text-sm mb-3">
                  {t('debug.no_gamepads')}
                </p>
              )}
              <button
                onClick={stopGamepadListening}
                className="btn-secondary text-sm"
              >
                {t('debug.stop_listening')}
              </button>
            </div>
          ) : (
            <div>
              <p className="text-text-muted mb-3">
                {t('debug.gamepad_description')}
              </p>
              <button
                onClick={startGamepadListening}
                className="btn-primary flex items-center gap-2"
              >
                <Gamepad2 size={16} />
                {t('debug.start_gamepad')}
              </button>
            </div>
          )}
        </div>

        {/* Media Keys Card */}
        <div className="card-elevated p-4">
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            <Keyboard size={20} />
            {t('debug.media_keys')}
          </h2>

          {mediaKeysListening ? (
            <div>
              <p className="text-green-600 font-medium mb-2">
                {t('debug.media_keys_listening')}
              </p>
              <p className="text-text-muted text-sm mb-3">
                {t('debug.media_keys_hint')}
              </p>
              <button
                onClick={stopMediaKeysListening}
                className="btn-secondary text-sm"
              >
                {t('debug.stop_listening')}
              </button>
            </div>
          ) : (
            <div>
              <p className="text-text-muted mb-3">
                {t('debug.media_keys_description')}
              </p>
              <button
                onClick={startMediaKeysListening}
                className="btn-primary flex items-center gap-2"
              >
                <Keyboard size={16} />
                {t('debug.start_media_keys')}
              </button>
            </div>
          )}
        </div>

        {/* Jabra SDK Card */}
        <div className="card-elevated p-4">
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            <Headphones size={20} />
            Jabra SDK
          </h2>

          {jabraListening ? (
            <div>
              <p className="text-green-600 font-medium mb-2">
                Jabra SDK active - {jabraDevices.length} device(s) found
              </p>
              {jabraDevices.length > 0 && (
                <ul className="text-sm mb-3 space-y-2">
                  {jabraDevices.map((device, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="text-text-muted">{device.name}</span>
                      <button
                        onClick={() => subscribeToJabraButtons(device)}
                        className="text-xs bg-primary text-white px-2 py-1 rounded"
                      >
                        Listen for buttons
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                onClick={stopJabraListening}
                className="btn-secondary text-sm"
              >
                {t('debug.stop_listening')}
              </button>
            </div>
          ) : (
            <div>
              <p className="text-text-muted mb-3">
                Connect to Jabra headsets via WebHID. Requires <strong>USB-C cable connection</strong> (Bluetooth not supported).
              </p>
              <button
                onClick={initializeJabra}
                disabled={jabraInitializing}
                className="btn-primary flex items-center gap-2"
              >
                {jabraInitializing ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Initializing...
                  </>
                ) : (
                  <>
                    <Headphones size={16} />
                    Initialize Jabra SDK
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Voice Trigger Test Card */}
        <div className="card-elevated p-4">
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            {voiceTestListening ? <Mic size={20} className="text-green-500" /> : <MicOff size={20} />}
            Voice Trigger Test
            {useVosk && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Vosk</span>}
          </h2>

          {!voiceTestSupported ? (
            <p className="text-amber-600">
              Speech Recognition not supported in this browser
            </p>
          ) : voiceTestListening ? (
            <div>
              <p className="text-green-600 font-medium mb-2">
                {isVoskModelLoading
                  ? `Loading Vosk model... ${voskModelProgress || 0}%`
                  : `Listening${useVosk ? ' (Vosk)' : ''}... (check Event Log below)`}
              </p>
              {useVosk && (
                <div className="text-xs bg-gray-100 rounded p-2 mb-3 font-mono">
                  <div>Model: {isVoskModelReady ? '✓ Ready' : isVoskModelLoading ? 'Loading...' : 'Not loaded'}</div>
                  <div>Listening: {isVoskListening ? '✓ Active' : 'Inactive'}</div>
                  <div>Audio chunks: {voskAudioCount} {voskAudioCount > 0 ? '✓' : '(waiting...)'}</div>
                  <div>Transcripts: {voskTranscriptCount}</div>
                  {voskLastHeardText && <div>Last heard: "{voskLastHeardText}"</div>}
                  {voskDebugInfo && <div className="text-blue-600">Debug: {voskDebugInfo}</div>}
                  {voskError && <div className="text-red-600">Error: {voskError.message}</div>}
                </div>
              )}
              <p className="text-text-muted text-sm mb-3">
                {useVosk
                  ? 'Using Vosk Wasm speech recognition (works on Android)'
                  : 'Speak to see what the browser hears. Matches will be highlighted.'}
              </p>
              <button
                onClick={handleStopVoiceTest}
                className="btn-secondary text-sm"
              >
                Stop Listening
              </button>
            </div>
          ) : (
            <div>
              <p className="text-text-muted mb-3">
                Test speech recognition in isolation. Shows all transcripts and keyword matches without triggering recording.
                {useVosk && ' Using Vosk (Android) - first run downloads ~50MB model.'}
              </p>
              {!useVosk && (
                <div className="mb-3">
                  <label className="text-sm text-text-muted block mb-1">Language:</label>
                  <select
                    value={voiceTestLanguage}
                    onChange={(e) => setVoiceTestLanguage(e.target.value)}
                    className="border rounded px-2 py-1 text-sm"
                  >
                    <option value="de-DE">German (de-DE)</option>
                    <option value="en-US">English (en-US)</option>
                    <option value="en-GB">English UK (en-GB)</option>
                  </select>
                </div>
              )}
              <button
                onClick={handleStartVoiceTest}
                className="btn-primary flex items-center gap-2"
              >
                <Mic size={16} />
                Start Voice Test {useVosk && '(Vosk)'}
              </button>
            </div>
          )}
        </div>
        </div>
        {/* End Connection Options Grid */}

        {/* Event Log */}
        <div className="card-elevated p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold">{t('debug.event_log')}</h2>
            <button
              type="button"
              onClick={handleClearLog}
              className="text-sm text-primary hover:underline cursor-pointer"
            >
              {t('debug.clear')}
            </button>
          </div>
          <div className="bg-gray-900 text-green-400 font-mono text-xs p-3 rounded-lg h-64 overflow-y-auto">
            {eventLog.length === 0 ? (
              <p className="text-gray-500">{t('debug.waiting_for_events')}</p>
            ) : (
              eventLog.map(entry => (
                <div key={entry.id} className="mb-1">
                  <span className="text-gray-500">[{entry.timestamp}]</span>{' '}
                  {entry.message}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Audio Samples */}
        <div className="card-elevated p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Music size={20} />
              {t('debug.audio_samples')}
            </h2>
            {audioSamples.length > 0 && (
              <button
                onClick={loadAudioSamples}
                disabled={audioLoading}
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                <RefreshCw size={14} className={audioLoading ? 'animate-spin' : ''} />
                {t('debug.refresh')}
              </button>
            )}
          </div>

          {audioLoading && audioSamples.length === 0 ? (
            <div className="flex items-center gap-2 text-text-muted">
              <Loader2 size={16} className="animate-spin" />
              {t('debug.loading')}
            </div>
          ) : audioSamples.length > 0 ? (
            <div className="max-h-64 overflow-y-auto">
              <ul className="space-y-2">
                {audioSamples.map((sample) => (
                  <li key={sample.key} className="flex items-center gap-2 text-sm bg-gray-50 p-2 rounded">
                    <button
                      onClick={() => playAudioSample(sample)}
                      className="p-1 rounded-full bg-primary text-white hover:bg-primary-dark"
                      title={currentlyPlaying === sample.key ? t('debug.pause') : t('debug.play')}
                    >
                      {currentlyPlaying === sample.key ? (
                        <Pause size={14} />
                      ) : (
                        <Play size={14} />
                      )}
                    </button>
                    <span className="flex-1 truncate text-text-muted" title={sample.name}>
                      {sample.name.replace('mixed_', '').replace('.mp3', '')}
                    </span>
                    <span className="text-xs text-gray-400">
                      {formatFileSize(sample.size)}
                    </span>
                    <button
                      onClick={() => downloadAudioSample(sample)}
                      className="p-1 rounded hover:bg-gray-200"
                      title={t('debug.download')}
                    >
                      <Download size={14} className="text-gray-600" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-text-muted text-sm">
              {t('debug.audio_samples_description')}
            </p>
          )}
        </div>
      </main>
    </div>
  );
};

export default DebugConsole;
